import { useEffect, useMemo, useState } from "react"
import { useAuth0 } from "@auth0/auth0-react"
import { useAuth0AccessToken } from "@/hooks/useAuth0AccessToken"
import { Button } from "@/components/ui/button"
import { StatusSelect } from "@/components/ui/status-select"
import {
  EmailSendDialog,
  type EmailRecipient,
  type SchedulingOption,
} from "@/components/email-send-dialog"
import { Card, CardAction, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Link } from "react-router-dom"
import { sendCandidateInvite, type SchedulePayload } from "@/lib/email/send-candidate-invite"
import { useEmailTemplates } from "@/hooks/supabase/useEmailTemplates"
import { shouldHideCompanyInEmail } from "@/lib/pipeline-stage"
import {
  DEFAULT_OFFICE_LOCATION,
  OFFICE_LOCATIONS,
  buildOfficeFooter,
  isOfficeLocation,
  type OfficeLocation,
} from "@/constants/office-locations"
import {
  candidateStages,
  getCandidateStage,
  getHubspotStageSlugCandidates,
  type CandidateStatusId,
} from "@/types/candidate"
import type { Template } from "@/types/email"
import type { CandidateApplicationResponse } from "@/types/vacancy"
import { Check, ChevronsUpDown, Mail, OctagonX, Send } from "lucide-react"
import AssociationTag from "@/components/association-tag"
import { cn } from "@/lib/utils"
import { slugifyTagLabel } from "@/lib/tag-utils"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"

export type CandidateVacancyAssignment = {
  id: string
  vacancyId?: string | null
  title?: string | null
  company?: string | null
  location?: string | null
  vakgebied?: string | null
  status: CandidateStatusId
  statusLabel?: string | null
  applicationId?: string | number | null
}

type MarkerMap = Record<string, string>

const FALLBACK_BADGE_BG = "#E2E8F0"
const FALLBACK_BADGE_TEXT = "#0F172A"

const sanitizeHex = (hex: string) => hex.trim().replace("#", "")

const expandShorthandHex = (hex: string) =>
  hex.length === 3
    ? hex
      .split("")
      .map((char) => char + char)
      .join("")
    : hex

const parseHexColor = (hex: string) => {
  const sanitized = expandShorthandHex(sanitizeHex(hex))

  if (!/^([0-9A-F]{6})$/i.test(sanitized)) {
    return null
  }

  const value = parseInt(sanitized, 16)

  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  }
}

const getReadableTextColor = (hex: string) => {
  const rgb = parseHexColor(hex)

  if (!rgb) return FALLBACK_BADGE_TEXT

  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255

  return luminance > 0.6 ? "#0F172A" : "#FFFFFF"
}

const computeBadgeColors = (hex?: string | null) => {
  if (!hex) {
    return {
      backgroundColor: FALLBACK_BADGE_BG,
      textColor: FALLBACK_BADGE_TEXT,
    }
  }
  const withHash = hex.startsWith("#") ? hex : `#${hex}`
  return {
    backgroundColor: withHash,
    textColor: getReadableTextColor(withHash),
  }
}

const splitNameForMarkers = (input?: string | null) => {
  if (!input) {
    return { first: null, last: null }
  }
  const segments = input.trim().split(/\s+/).filter(Boolean)
  if (segments.length === 0) {
    return { first: null, last: null }
  }
  const [first, ...rest] = segments
  return {
    first: first ?? null,
    last: rest.length > 0 ? rest.join(" ") : null,
  }
}

const TAG_STAGE_BY_SLUG = (() => {
  const map = new Map<string, CandidateStatusId>()
  candidateStages.forEach((stage) => {
    const slugs = getHubspotStageSlugCandidates(stage.id)
    slugs.forEach((slug) => {
      const normalized = slugifyTagLabel(slug)
      if (normalized && !map.has(normalized)) {
        map.set(normalized, stage.id)
      }
    })
  })
  return map
})()

type TagOption = {
  label: string
  slug: string
  stageId: CandidateStatusId | null
}

const resolveStageForTag = (label: string, slug?: string | null): CandidateStatusId | null => {
  const normalized = slugifyTagLabel(slug ?? label)
  if (!normalized) {
    return null
  }
  return TAG_STAGE_BY_SLUG.get(normalized) ?? null
}

const stripOpdrachtgeverFromLabel = (value: string) => {
  const cleaned = value.replace(/\s+opdrachtgever$/i, "").trim()
  return cleaned.length > 0 ? cleaned : value
}

const stripOpdrachtgeverFromSlug = (value?: string | null) => {
  if (!value) {
    return value ?? null
  }
  const cleaned = value.replace(/-opdrachtgever$/i, "")
  return cleaned.length > 0 ? cleaned : value
}

const resolvePresetSchedule = (option: SchedulingOption) => {
  const now = new Date()

  if (option === "one-hour") {
    const inOneHour = new Date(now.getTime() + 60 * 60 * 1000)
    return inOneHour.toISOString()
  }

  if (option === "tomorrow-morning") {
    const tomorrow = new Date(now)
    tomorrow.setDate(tomorrow.getDate() + 1)
    tomorrow.setHours(9, 0, 0, 0)
    return tomorrow.toISOString()
  }

  return now.toISOString()
}

type CandidateSidebarProps = {
  status: CandidateStatusId
  vacancies?: CandidateVacancyAssignment[]
  vacanciesLoading?: boolean
  onStatusChange: (status: CandidateStatusId) => void
  candidateName?: string | null
  candidateEmail?: string | null
  applicationId?: string | number | null
  candidateApplication?: CandidateApplicationResponse | null
  candidateId?: string | null
  onSendEmail?: (payload: {
    template: Template
    body: string
    recipients: EmailRecipient[]
  }) => void
  manualTags?: string[]
  onManualTagsSave?: (tags: string[]) => Promise<void> | void
  manualTagsSaving?: boolean
  manualTagsError?: string | null
}

const CandidateSidebar = ({
  status,
  vacancies = [],
  vacanciesLoading = false,
  onStatusChange,
  // candidateName,
  // candidateEmail,
  applicationId: applicationIdProp = null,
  candidateApplication = null,
  candidateId = null,
  onSendEmail,
  manualTags = [],
  onManualTagsSave,
  manualTagsSaving = false,
  manualTagsError = null,
}: CandidateSidebarProps) => {
  const { user } = useAuth0()
  const getAccessToken = useAuth0AccessToken()
  const { templates, loading: templatesLoading } = useEmailTemplates()
  const hasTemplates = templates.length > 0

  const actorName = user?.nickname ?? null
  const actorEmail = user?.email ?? null
  const actorId = user?.sub ?? null
  const team = user?.["https://ats.siebertwassink.nl/team"] as string | undefined
  const isOfficeTeam = team === "office"
  const officeClaim = user?.["https://ats.siebertwassink.nl/office"]
  const userOfficeLocation: OfficeLocation = isOfficeLocation(officeClaim)
    ? officeClaim
    : DEFAULT_OFFICE_LOCATION
  const OFFICE_FROM_EMAIL = import.meta.env.VITE_OFFICE_FROM_EMAIL ?? "office@siebertwassink.nl"

  const hideCompanyInEmailDialog = shouldHideCompanyInEmail(
    candidateApplication?.vacancy?.pipelineStage ?? null
  )

  const [emailDialogOpen, setEmailDialogOpen] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<Template>("Custom")
  const [messageBody, setMessageBody] = useState("")
  const [subjectLine, setSubjectLine] = useState("")
  const [recipients, setRecipients] = useState<EmailRecipient[]>([])
  const [fromEmail, setFromEmail] = useState<string>(() =>
    isOfficeTeam ? OFFICE_FROM_EMAIL : actorEmail || ""
  )
  const [emailLocation, setEmailLocation] = useState<OfficeLocation>(userOfficeLocation)
  const [isSending, setIsSending] = useState(false)
  const [sendError, setSendError] = useState<string | null>(null)
  const [selectedTags, setSelectedTags] = useState<string[]>(manualTags)
  const [tagPickerOpen, setTagPickerOpen] = useState(false)
  const [showAllTags, setShowAllTags] = useState(false)
  const tagOptions = useMemo<TagOption[]>(() => {
    const map = new Map<string, TagOption>()
    const addOption = (
      label?: string | null,
      slug?: string | null,
      options?: { stripOpdrachtgever?: boolean }
    ) => {
      if (!label) {
        return
      }
      const trimmed = label.trim()
      if (!trimmed) {
        return
      }
      const shouldStrip = options?.stripOpdrachtgever ?? false
      const displayLabel = shouldStrip ? stripOpdrachtgeverFromLabel(trimmed) : trimmed
      const slugSource = shouldStrip ? stripOpdrachtgeverFromSlug(slug ?? trimmed) : slug ?? trimmed
      const normalizedSlug = slugifyTagLabel(slugSource ?? displayLabel)
      if (!normalizedSlug || map.has(normalizedSlug)) {
        return
      }
      map.set(normalizedSlug, {
        label: displayLabel,
        slug: normalizedSlug,
        stageId: resolveStageForTag(displayLabel, normalizedSlug),
      })
    }
    candidateApplication?.associationLabels?.forEach((option) =>
      addOption(option.label, option.slug)
    )
    manualTags.forEach((tag) => addOption(tag, tag))
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, "nl", { sensitivity: "base" }))
  }, [candidateApplication?.associationLabels, manualTags])

  const filteredTagOptions = useMemo(() => {
    if (!tagOptions.length) {
      return tagOptions
    }
    const matching = tagOptions.filter(
      (option) => !option.stageId || option.stageId === status
    )
    return matching.length > 0 ? matching : tagOptions
  }, [status, tagOptions])

  const tagPickerOptions = showAllTags ? tagOptions : filteredTagOptions
  const canShowAllTags = tagOptions.length > filteredTagOptions.length
  const canLimitToStatusTags =
    showAllTags &&
    tagOptions.some((option) => option.stageId && option.stageId === status)

  useEffect(() => {
    setSelectedTags(manualTags)
  }, [manualTags])

  useEffect(() => {
    if (manualTagsSaving) {
      setTagPickerOpen(false)
      setShowAllTags(false)
    }
  }, [manualTagsSaving])

  useEffect(() => {
    setShowAllTags(false)
  }, [status])

  useEffect(() => {
    if (!hasTemplates) {
      return
    }
    let activeTemplate = templates.find(
      (template) => template.template === selectedTemplate
    )
    if (!activeTemplate) {
      const fallbackTemplate = templates[0]
      if (!fallbackTemplate) {
        return
      }
      setSelectedTemplate(fallbackTemplate.template)
      activeTemplate = fallbackTemplate
    }
    const isCustomTemplate = activeTemplate.template === "Custom"
    setMessageBody((current) => {
      if (isCustomTemplate) {
        return current && current.trim().length > 0 ? current : activeTemplate.body
      }
      return activeTemplate.body
    })
    setSubjectLine((current) => {
      if (isCustomTemplate) {
        return current && current.trim().length > 0
          ? current
          : (activeTemplate.subject ?? "")
      }
      return activeTemplate.subject ?? ""
    })
  }, [selectedTemplate, templates, hasTemplates])

  useEffect(() => {
    if (!emailDialogOpen) {
      setSendError(null)
    }
  }, [emailDialogOpen])

  const toggleTagSelection = (tag: string) => {
    const computeNext = (current: string[]) =>
      current.includes(tag)
        ? current.filter((value) => value !== tag)
        : [...current, tag]

    if (!onManualTagsSave) {
      setSelectedTags((current) => computeNext(current))
      return
    }

    const next = computeNext(selectedTags)
    setSelectedTags(next)
    onManualTagsSave(next)
  }

  const emailContext = useMemo(() => {
    const applications = candidateApplication?.applications ?? []
    const rawCompanyName = candidateApplication?.company?.name ?? null
    const rawCompanyLocation = candidateApplication?.company?.location ?? null
    const safeCompanyName = hideCompanyInEmailDialog ? null : rawCompanyName
    const safeCompanyLocation = hideCompanyInEmailDialog ? null : rawCompanyLocation
    if (!applications.length) {
      return {
        application: null,
        applicationId: null,
        candidateName: null,
        candidateEmail: null,
        jobTitle:
          candidateApplication?.vacancy?.title ??
          candidateApplication?.vacancy?.vacatureNaam ??
          null,
        companyName: safeCompanyName,
        companyLocation: safeCompanyLocation,
        vacancyName:
          candidateApplication?.vacancy?.title ??
          candidateApplication?.vacancy?.vacatureNaam ??
          null,
      }
    }
    const normalizedApplicationId =
      applicationIdProp !== null && applicationIdProp !== undefined
        ? String(applicationIdProp)
        : null
    const matchedIndex = normalizedApplicationId
      ? applications.findIndex(
        (application) => application.applicationId === normalizedApplicationId
      )
      : -1
    const primaryIndex = matchedIndex >= 0 ? matchedIndex : 0
    const primaryApplication = applications[primaryIndex] ?? applications[0] ?? null
    const candidateData = primaryApplication?.candidate ?? null
    const candidateName =
      candidateData?.name ??
      [candidateData?.firstName, candidateData?.lastName].filter(Boolean).join(" ").trim() ??
      null
    const candidateEmailResolved = candidateData?.email ?? null
    const jobTitleResolved =
      candidateData?.jobTitle ??
      candidateApplication?.vacancy?.title ??
      candidateApplication?.vacancy?.vacatureNaam ??
      null
    const vacancyNameResolved =
      candidateApplication?.vacancy?.title ??
      candidateApplication?.vacancy?.vacatureNaam ??
      null
    const companyResolved = safeCompanyName
    const companyLocationResolved = safeCompanyLocation
    return {
      application: primaryApplication,
      applicationId: primaryApplication?.applicationId ?? null,
      candidateName,
      candidateEmail: candidateEmailResolved,
      jobTitle: jobTitleResolved,
      companyName: companyResolved,
      companyLocation: companyLocationResolved,
      vacancyName: vacancyNameResolved,
    }
  }, [
    applicationIdProp,
    candidateApplication?.applications,
    candidateApplication?.company?.name,
    candidateApplication?.company?.location,
    candidateApplication?.vacancy?.title,
    candidateApplication?.vacancy?.vacatureNaam,
    hideCompanyInEmailDialog,
  ])
  const {
    application: emailPrimaryApplication,
    applicationId: emailApplicationId,
    candidateName: emailCandidateName,
    candidateEmail: emailCandidateEmail,
    jobTitle: emailJobTitle,
    companyName: emailCompanyName,
    companyLocation: emailCompanyLocation,
    vacancyName: emailVacancyName,
  } = emailContext

  const primaryRecipient = useMemo<EmailRecipient | null>(() => {
    if (!emailCandidateEmail) {
      return null
    }
    return {
      id: "candidate-primary",
      name:
        (emailCandidateName && emailCandidateName.trim().length > 0
          ? emailCandidateName
          : emailCandidateEmail) ?? emailCandidateEmail,
      email: emailCandidateEmail,
    }
  }, [emailCandidateEmail, emailCandidateName])

  const markerValues = useMemo<MarkerMap>(() => {
    type AllowedKey =
      | "firstName"
      | "lastName"
      | "recipientName"
      | "jobTitle"
      | "company"
      | "companyLocation"
      | "phoneNumber"
      | "tel"
      | "reception"
      | "vacancyName"
      | "senderEmail"
      | "Locatie"
      | "Advisor"
      | "AdvisorOrOffice"
      | "officeFooter"

    const markers = new Map<AllowedKey, string>()

    const candidateFromApplication = emailPrimaryApplication?.candidate ?? null
    const resolvedCandidateName =
      emailCandidateName ??
      candidateFromApplication?.name ??
      null
    const resolvedCandidateEmail = emailCandidateEmail ?? null
    const { first: candidateFirstName, last: candidateLastName } = splitNameForMarkers(
      resolvedCandidateName
    )

    const setMarker = (key: AllowedKey, value: unknown) => {
      if (value === null || value === undefined) {
        return
      }
      const normalized = typeof value === "string" ? value.trim() : String(value)
      if (!normalized) {
        return
      }
      markers.set(key, normalized)
    }

    const actorEmailValue = actorEmail

    setMarker("firstName", candidateFirstName)
    setMarker("lastName", candidateLastName)
    setMarker("recipientName", resolvedCandidateName ?? resolvedCandidateEmail ?? null)

    setMarker("jobTitle", emailJobTitle)

    setMarker("company", emailCompanyName)
    setMarker("companyLocation", emailCompanyLocation)

    setMarker("vacancyName", emailVacancyName)

    // Advisor marker from vacancy (HubSpot 'selectieadviseur')
    setMarker("Advisor", candidateApplication?.vacancy?.advisor ?? null)
    // AdvisorOrOffice: prefer advisor, otherwise when user team === 'office' use 'team office'
    if (candidateApplication?.vacancy?.advisor) {
      setMarker("AdvisorOrOffice", candidateApplication.vacancy.advisor)
    } else if (team === "office") {
      setMarker("AdvisorOrOffice", `${team} office`)
    }

    setMarker("senderEmail", fromEmail || actorEmailValue || "office@siebertwassink.nl")
    setMarker("Locatie", OFFICE_LOCATIONS[emailLocation].address)
    setMarker("phoneNumber", OFFICE_LOCATIONS[emailLocation].phone)
    setMarker("tel", OFFICE_LOCATIONS[emailLocation].tel)
    setMarker("reception", OFFICE_LOCATIONS[emailLocation].reception)
    setMarker("officeFooter", buildOfficeFooter(emailLocation))

    return Object.fromEntries(markers.entries())
  }, [
    actorEmail,
    fromEmail,
    emailLocation,
    emailCandidateEmail,
    emailCandidateName,
    emailCompanyName,
    emailCompanyLocation,
    emailJobTitle,
    emailVacancyName,
    emailPrimaryApplication,
  ])

  const buildRecipientList = () => {
    if (!primaryRecipient) {
      return []
    }
    const markersForRecipient =
      Object.keys(markerValues).length > 0 ? markerValues : null
    return [
      {
        ...primaryRecipient,
        applicationId: emailApplicationId,
        candidateId: candidateId ?? emailPrimaryApplication?.candidateId ?? null,
        vacancyId: emailPrimaryApplication?.vacancyId ?? null,
        markers: markersForRecipient,
        vacatureNaam: emailVacancyName ?? null,
      },
    ]
  }

  const queueEmail = async (schedule?: SchedulePayload | null) => {
    const finalRecipients =
      recipients.length > 0 ? recipients : buildRecipientList()
    setRecipients(finalRecipients)

    setIsSending(true)
    setSendError(null)
    try {
      const jobTitle = emailJobTitle
      const company = emailCompanyName
      const applicationIdRaw = emailApplicationId
      const applicationKey =
        typeof applicationIdRaw === "string" ? applicationIdRaw : null
      const templateMeta =
        templates.find((item) => item.template === selectedTemplate) ??
        templates[0] ??
        null

      const resolvedSubject =
        subjectLine.trim().length > 0
          ? subjectLine.trim()
          : templateMeta?.subject ?? null
      const resolvedTemplateName = templateMeta?.template ?? selectedTemplate

      const accessToken = await getAccessToken()
      const queueId = await sendCandidateInvite({
        template: resolvedTemplateName,
        templateId: templateMeta?.id ?? null,
        body: messageBody,
        recipients: finalRecipients,
        candidateName: emailCandidateName,
        jobTitle,
        company,
        applicationId: applicationIdRaw,
        applicationKey,
        subject: resolvedSubject,
        schedule,
        actor: {
          name: actorName,
          email: actorEmail,
          userId: actorId,
        },
        markers: {
          Locatie: OFFICE_LOCATIONS[emailLocation].address,
          phoneNumber: OFFICE_LOCATIONS[emailLocation].phone,
          tel: OFFICE_LOCATIONS[emailLocation].tel,
          reception: OFFICE_LOCATIONS[emailLocation].reception,
          officeFooter: buildOfficeFooter(emailLocation),
        },
        fromEmail,
        accessToken,
        hideCompanyInEmail: hideCompanyInEmailDialog,
      })
      onSendEmail?.({
        template: resolvedTemplateName,
        body: messageBody,
        recipients: finalRecipients,
      })
      return queueId
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Onbekende fout bij het versturen van de e-mail"
      setSendError(message)
      throw error
    } finally {
      setIsSending(false)
    }
  }

  const handleStatusSelect = (value: CandidateStatusId) => {
    onStatusChange(value)
  }

  const openEmailDialog = (template: Template) => {
    const matchedTemplate =
      templates.find((item) => item.template === template) ??
      templates[0] ??
      null
    const resolvedTemplateName = matchedTemplate?.template ?? template
    setSelectedTemplate(resolvedTemplateName)
    setMessageBody(matchedTemplate?.body ?? "")
    setSubjectLine(matchedTemplate?.subject ?? "")
    setRecipients(buildRecipientList())
    setEmailLocation(userOfficeLocation)
    setEmailDialogOpen(true)
  }

  const handleRemoveRecipient = (recipientId: string) => {
    setRecipients((current) => current.filter((recipient) => recipient.id !== recipientId))
  }

  const handleTemplateChange = (template: Template) => {
    setSelectedTemplate(template)
    const matchedTemplate = templates.find((item) => item.template === template)
    setSubjectLine(matchedTemplate?.subject ?? "")
    setMessageBody((current) => {
      if (template === "Custom") {
        return current && current.trim().length > 0 ? current : matchedTemplate?.body ?? ""
      }
      return matchedTemplate?.body ?? ""
    })
  }

  const handleMessageBodyChange = (body: string) => {
    setMessageBody(body)
  }

  const handleSubjectChange = (value: string) => {
    setSubjectLine(value)
  }

  const fromEmailOptions = useMemo(() => {
    if (isOfficeTeam) {
      return [OFFICE_FROM_EMAIL]
    }
    const options = new Set<string>()
    if (fromEmail) {
      options.add(fromEmail)
    }
    if (actorEmail) {
      options.add(actorEmail)
    }
    return Array.from(options)
  }, [OFFICE_FROM_EMAIL, actorEmail, fromEmail, isOfficeTeam])

  const fromEmailDisabled = true

  useEffect(() => {
    if (isOfficeTeam) {
      setFromEmail(OFFICE_FROM_EMAIL)
      return
    }
    if (!fromEmail && actorEmail) {
      setFromEmail(actorEmail)
    }
  }, [actorEmail, fromEmail, isOfficeTeam, OFFICE_FROM_EMAIL])

  useEffect(() => {
    if (recipients.length === 0) {
      return
    }
    const officeDetails = OFFICE_LOCATIONS[emailLocation]
    setRecipients((current) =>
      current.map((recipient) => ({
        ...recipient,
        markers: {
          ...(recipient.markers ?? {}),
          Locatie: officeDetails.address,
          phoneNumber: officeDetails.phone,
          tel: officeDetails.tel,
          reception: officeDetails.reception,
          officeFooter: buildOfficeFooter(emailLocation),
          senderEmail: fromEmail || recipient.markers?.senderEmail || actorEmail || "office@siebertwassink.nl",
          ...(candidateApplication?.vacancy?.advisor
            ? { Advisor: candidateApplication.vacancy.advisor }
            : {}),
          ...(
            candidateApplication?.vacancy?.advisor
              ? { AdvisorOrOffice: candidateApplication.vacancy.advisor }
              : team === "office"
              ? { AdvisorOrOffice: `${team} office` }
              : {}
          ),
        },
      }))
    )
  }, [actorEmail, emailLocation, fromEmail, recipients.length, candidateApplication?.vacancy?.advisor, team])

  const handleSendEmail = async () => {
    try {
      await queueEmail(null)
      setEmailDialogOpen(false)
    } catch (error) {
      console.error("[candidate-sidebar] send email failed", error)
    }
  }

  const handleScheduleOption = async (option: SchedulingOption) => {
    const scheduledAt = resolvePresetSchedule(option)
    await queueEmail({
      kind: "preset",
      option,
      scheduledAt,
    })
  }

  const handleScheduleCustom = async (scheduledAt: Date) => {
    await queueEmail({
      kind: "custom",
      scheduledAt: scheduledAt.toISOString(),
    })
  }

  return (
    <aside className="flex w-full flex-col lg:max-w-xs lg:flex-shrink-0 rounded-xl border border-slate-200">
      <section className="border-b border-slate-200 p-6">
        <div className="flex flex-col gap-3">
          <h2 className="text-xl font-semibold tracking-wide text-black">
            Status
          </h2>
          <div className="flex flex-col gap-2 text-sm font-medium text-slate-700">
            <StatusSelect
              className="w-fit"
              value={status}
              onChange={handleStatusSelect}
              options={candidateStages}
            />
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Tags
            </span>
            <div className="flex items-center gap-2">
              <Popover
                open={tagPickerOpen}
                onOpenChange={(nextOpen) => {
                  if (
                    manualTagsSaving ||
                    !onManualTagsSave ||
                    tagPickerOptions.length === 0
                  ) {
                    return
                  }
                  setTagPickerOpen(nextOpen)
                }}
              >
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    role="combobox"
                    aria-expanded={tagPickerOpen}
                    className="flex w-full items-center justify-between gap-2"
                    disabled={
                      manualTagsSaving ||
                      !onManualTagsSave ||
                      tagPickerOptions.length === 0
                    }
                  >
                    <span className="truncate text-left">
                      {tagPickerOptions.length === 0
                        ? tagOptions.length === 0
                          ? "Geen tagopties beschikbaar"
                          : "Geen tags gevonden"
                        : selectedTags.length > 0
                          ? `${selectedTags.length} tag${selectedTags.length === 1 ? "" : "s"} geselecteerd`
                          : "Zoek en selecteer tags"}
                    </span>
                    <ChevronsUpDown className="h-4 w-4 text-slate-500" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-72" align="start">
                  <Command shouldFilter>
                    <CommandInput placeholder="Zoek een tag..." />
                    <CommandList>
                      <CommandEmpty>
                        {tagOptions.length === 0
                          ? "Geen tags beschikbaar."
                          : showAllTags
                            ? "Geen tags gevonden."
                            : "Geen tags voor deze status."}
                      </CommandEmpty>
                      <CommandGroup heading="Beschikbare tags">
                        {tagPickerOptions.map((option) => {
                          const isSelected = selectedTags.includes(option.label)
                          return (
                            <CommandItem
                              key={option.slug}
                              value={option.label}
                              onSelect={() => {
                                if (manualTagsSaving) {
                                  return
                                }
                                toggleTagSelection(option.label)
                              }}
                            >
                              <Check
                                className={cn(
                                  "h-4 w-4",
                                  isSelected ? "opacity-100 text-primary" : "opacity-0"
                                )}
                              />
                              <span>{option.label}</span>
                            </CommandItem>
                          )
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                  <div className="border-t border-slate-100" />
                  <div className="p-1 hover:bg-slate-100 flex flex-col gap-1">
                    {tagPickerOptions.length === 0 ? (
                      <p className="text-xs text-slate-500">
                        {tagOptions.length === 0
                          ? "Geen tags beschikbaar."
                          : showAllTags
                            ? "Geen tags gevonden."
                            : "Geen tags voor deze status."}
                      </p>
                    ) : null}
                    {canShowAllTags && !showAllTags ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="w-full text-primary"
                        onClick={() => setShowAllTags(true)}
                        disabled={manualTagsSaving}
                      >
                        Meer tags
                      </Button>
                    ) : null}
                    {canLimitToStatusTags ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="w-full text-primary"
                        onClick={() => setShowAllTags(false)}
                        disabled={manualTagsSaving}
                      >
                        Toon alleen status-tags
                      </Button>
                    ) : null}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            {selectedTags.length ? (
              <div className="flex flex-wrap gap-1">
                {selectedTags.map((tag) => (
                  <AssociationTag
                    key={tag}
                    label={tag}
                    onRemove={onManualTagsSave ? () => toggleTagSelection(tag) : undefined}
                    disabled={manualTagsSaving}
                    className="pr-2"
                  />
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-500">Nog geen tags toegevoegd.</p>
            )}
            {manualTagsError ? (
              <p className="text-xs text-red-500">{manualTagsError}</p>
            ) : null}
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200 p-6">
        <h2 className="text-xl font-semibold tracking-wide text-black">
          Vacatures
        </h2>
        {vacanciesLoading && vacancies.length === 0 ? (
          <div className="flex flex-col gap-3 mt-4" aria-hidden="true">
            {Array.from({ length: 2 }).map((_, index) => (
              <Card
                key={`vacancy-skeleton-${index}`}
                className="text-card-foreground flex flex-col gap-6 border border-slate-200 p-3 rounded-sm bg-primary-bg shadow-none"
              >
                <CardHeader className="uto-rows-min grid-rows-[auto_auto] has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6 flex flex-col items-start justify-between gap-4 p-0">
                  <div className="flex flex-col gap-2">
                    <span className="h-4 w-36 skeleton-shimmer rounded-sm" aria-hidden="true" />
                    <span className="h-3 w-24 skeleton-shimmer rounded-sm" aria-hidden="true" />
                  </div>
                  <CardAction>
                    <span className="h-6 w-16 skeleton-shimmer rounded-full" aria-hidden="true" />
                  </CardAction>
                </CardHeader>
              </Card>
            ))}
          </div>
        ) : vacancies.length > 0 ? (
          <div className="flex flex-col gap-3 mt-4">
            {vacancies.map((vacancy) => {
              const stage = getCandidateStage(vacancy.status)
              const { backgroundColor, textColor } = computeBadgeColors(stage?.color)
              const statusLabel = stage?.label ?? vacancy.statusLabel ?? vacancy.status
              const title =
                vacancy.title && vacancy.title.trim().length > 0
                  ? vacancy.title
                  : "Vacature"
              const metaParts = [vacancy.company, vacancy.location]
                .filter((value): value is string => Boolean(value && value.trim().length > 0))
              const badge = (
                <span
                  className="flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold"
                  style={{
                    backgroundColor,
                    color: textColor,
                  }}
                >
                  {statusLabel}
                </span>
              )
              const card = (
                <Card className="text-card-foreground flex flex-col gap-6 border border-slate-200 p-3 rounded-sm bg-primary-bg shadow-none">
                  <CardHeader className="auto-rows-min grid-rows-[auto_auto] has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6 flex flex-col items-start justify-between gap-4 p-0">
                    <div className="flex flex-col gap-1">
                      <CardTitle className="text-sm font-semibold text-black">
                        {title}
                      </CardTitle>
                      {metaParts.length ? (
                        <CardDescription className="text-xs text-slate-600">
                          {metaParts.join(" • ")}
                        </CardDescription>
                      ) : null}
                    </div>
                    <CardAction>
                      {badge}
                    </CardAction>
                  </CardHeader>
                </Card>
              )

              if (vacancy.vacancyId && candidateId) {
                return (
                  <Link
                    key={vacancy.id}
                    to={`/vacature/${vacancy.vacancyId}/kandidaat/${candidateId}`}
                    state={{
                      vacancyId: vacancy.vacancyId,
                      vacancySlug: vacancy.vacancyId,
                      vacancyTitle: vacancy.title ?? null,
                      companyName: vacancy.company ?? null,
                      originView: "candidate-related",
                    }}
                    className="block no-underline text-inherit"
                  >
                    {card}
                  </Link>
                )
              }

              if (vacancy.vacancyId) {
                return (
                  <Link
                    key={vacancy.id}
                    to={`/vacature/${vacancy.vacancyId}`}
                    className="block no-underline text-inherit"
                  >
                    {card}
                  </Link>
                )
              }

              return (
                <Card
                  key={vacancy.id}
                  className="rounded-sm border border-primary/30 bg-primary-bg/95 p-3 text-card-foreground shadow-[0_12px_28px_-18px_rgba(15,23,42,0.55)] ring-1 ring-primary/10 transition-shadow hover:shadow-[0_16px_38px_-16px_rgba(15,23,42,0.6)]"
                >
                  <CardHeader className="gap-4 px-0 py-0 has-data-[slot=card-action]:grid-cols-[1fr_auto]">
                    <div className="flex flex-col gap-1">
                      <CardTitle className="text-sm font-semibold text-black">
                        {title}
                      </CardTitle>
                      {metaParts.length ? (
                        <CardDescription className="text-xs text-slate-600">
                          {metaParts.join(" • ")}
                        </CardDescription>
                      ) : null}
                    </div>
                    <CardAction>
                      {badge}
                    </CardAction>
                  </CardHeader>
                </Card>
              )
            })}
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-slate-200 px-4 py-6 text-sm text-slate-500">
            Geen vacatures gekoppeld aan deze kandidaat.
          </div>
        )}
      </section>

      <section className="flex flex-col gap-3 p-6">
        <Button
          type="button"
          variant="default"
          style="pill"
          className="w-full"
          onClick={() => openEmailDialog("Bevestiging sollicitatie")}
          disabled={!hasTemplates || templatesLoading}
        >
          <Send />
          Uitnodiging versturen
        </Button>
        <Button
          type="button"
          variant="destructive"
          style="pill"
          className="w-full"
          onClick={() => openEmailDialog("Afwijsmail op cv")}
          disabled={!hasTemplates || templatesLoading}
        >
          <OctagonX />
          Afwijzing versturen
        </Button>
        <Button
          type="button"
          variant="outline"
          style="pill"
          className="w-full"
          onClick={() => openEmailDialog("Custom")}
          disabled={!hasTemplates || templatesLoading}
        >
          <Mail />
          Aangepaste email
        </Button>
      </section>
      <EmailSendDialog
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
        recipients={recipients}
        onRemoveRecipient={handleRemoveRecipient}
        templates={templates}
        selectedTemplate={selectedTemplate}
        onTemplateChange={handleTemplateChange}
        messageBody={messageBody}
        onMessageBodyChange={handleMessageBodyChange}
        subjectLine={subjectLine}
        onSubjectChange={handleSubjectChange}
        onSchedule={handleScheduleOption}
        onScheduleCustom={handleScheduleCustom}
        isSending={isSending}
        errorMessage={sendError}
        onSend={handleSendEmail}
        availableMarkers={markerValues}
        hideCompanyFallback={hideCompanyInEmailDialog}
        location={emailLocation}
        onLocationChange={setEmailLocation}
        fromEmail={fromEmail}
        fromEmailOptions={fromEmailOptions}
        onFromEmailChange={setFromEmail}
        fromEmailDisabled={fromEmailDisabled}
      />
    </aside>
  )
}

export default CandidateSidebar
